import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { useGetDailyStoreStocksQuery, useDeleteDailyStoreStockMutation } from '../slices/dailyStoreStocksApiSlice';
import { toast } from 'react-toastify';
import { useState } from 'react';

const StaffDailyStoreStock = () => {
  // State variables for filtering, pagination, and date range
  const [filter, setFilter] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Fetch paginated dailyStoreStocks data
  const { data, isLoading, error, refetch } = useGetDailyStoreStocksQuery(pageNumber);

  // Mutation for deleting dailyStoreStocks
  const [deleteDailyStoreStock, { isLoading: loadingDelete }] = useDeleteDailyStoreStockMutation();

  // Handle dailyStoreStock deletion
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this dailyStoreStock?')) {
      try {
        await deleteDailyStoreStock(id).unwrap();
        toast.success('DailyStoreStock deleted successfully!');
        refetch(); // Refetch dailyStoreStocks after deletion
      } catch (err) {
        toast.error(err?.data?.message || 'Failed to delete dailyStoreStock.');
      }
    }
  };

  // Filter dailyStoreStocks based on date range and search criteria
  const filterDailyStoreStocks = () => {
    if (!data?.dailyStoreStocks) return [];

    // 1) Filter by date range
    let filteredDailyStoreStocks = data.dailyStoreStocks.filter((dailyStoreStock) => {
      const dailyStoreStockDate = new Date(dailyStoreStock.date);
      return (
        (!startDate || dailyStoreStockDate >= new Date(startDate)) &&
        (!endDate || dailyStoreStockDate <= new Date(endDate))
      );
    });

    // 2) Apply general search filter against dailyStoreStock note and product names within dailyStoreStockItems.
    if (filter.trim()) {
      const lowerFilter = filter.toLowerCase();
      filteredDailyStoreStocks = filteredDailyStoreStocks.filter((dailyStoreStock) => {
        const noteMatch = dailyStoreStock.note?.toLowerCase().includes(lowerFilter);
        const itemMatch = dailyStoreStock.dailyStoreStockItems.some((item) =>
          item.name.toLowerCase().includes(lowerFilter)
        );
        return noteMatch || itemMatch;
      });
    }

    // 3) Further filter dailyStoreStock items by product name ONLY using productSearch.
    if (productSearch.trim()) {
      const lowerProduct = productSearch.toLowerCase();
      filteredDailyStoreStocks = filteredDailyStoreStocks
        .map((dailyStoreStock) => {
          return {
            ...dailyStoreStock,
            dailyStoreStockItems: dailyStoreStock.dailyStoreStockItems.filter((item) =>
              item.name.toLowerCase().includes(lowerProduct)
            ),
          };
        })
        .filter((dailyStoreStock) => dailyStoreStock.dailyStoreStockItems.length > 0);
    }

    return filteredDailyStoreStocks;
  };

  // Calculate total dailyStoreStocks value based on filtered dailyStoreStocks
  const calculateTotalDailyStoreStocks = () => {
    return filterDailyStoreStocks().reduce((total, dailyStoreStock) => {
      const dailyStoreStockTotal = dailyStoreStock.dailyStoreStockItems.reduce(
        (subTotal, item) => subTotal + item.qty * item.price,
        0
      );
      return total + dailyStoreStockTotal;
    }, 0);
  };

  // Handle pagination changes
  const paginationHandler = (page) => {
    setPageNumber(page);
  };

  return (
    <>
      <Row className="align-items-center mb-3">
        <Col>
          <h1>DailyMiniStocks</h1>
        </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by product name or note"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            placeholder="Start Date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            placeholder="End Date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
        <Col md={6} className="text-end">
          <h4>Total DailyStoreStocks: {calculateTotalDailyStoreStocks().toFixed(2)}</h4>
        </Col>
      </Row>

      {/* Additional product-only search box */}
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by product name only"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
          />
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'An error occurred'}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT NAME</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>NOTE</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterDailyStoreStocks().map((dailyStoreStock) =>
                dailyStoreStock.dailyStoreStockItems.map((item) => (
                  <tr key={`${dailyStoreStock._id}-${item.product}`}>
                    <td>{new Date(dailyStoreStock.date).toLocaleDateString()}</td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>{(item.qty * item.price).toFixed(2)}</td>
                    <td>{dailyStoreStock.note || 'N/A'}</td>
                    <td>
                      {item.images && (
                        <img
                          src={item.images[0]}
                          alt="Thumbnail"
                          className="thumbnail-img me-2"
                          style={{
                            width: '40px',
                            height: '40px',
                            objectFit: 'cover',
                            borderRadius: '5px',
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/dailyStoreStock/${dailyStoreStock._id}`}
                        variant="light"
                        className="btn-sm"
                      >
                        Details
                      </Button>
                      <Button
                        style={{ display: 'none' }}
                        as={Link}
                        to={`/dailyStoreStock/${dailyStoreStock._id}/edit`}
                        variant="light"
                        className="btn-sm mx-2"
                      >
                        Edit
                      </Button>
                      <Button
                        style={{ display: 'none' }}
                        variant="danger"
                        className="btn-sm mx-2"
                        onClick={() => deleteHandler(dailyStoreStock._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default StaffDailyStoreStock;
